import React from "react";
import classNames from "classnames";
import { allocation } from "@planzone/dto";
import {
  useAllocateResources,
  useRemoveAllocation,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { ResourceAvatar } from "@common/resourceAvatar";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { ActivityWatcher } from "@common/activityWatcher";
import { AddAllocationIcon } from "./addAllocationIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function AllocationsList({ allocations, task, project }: Props) {
  const { isRequestPending, allocateResources } = useAllocateResources(task);
  const { removeAllocation } = useRemoveAllocation({ ...allocation, uuid: "" });

  return (
    <Safeguard useCases={[UseCases.allocateResources]} context={{ task }}>
      {({ canAllocateResources }) => (
        <div className={styles.allocationsList}>
          <VisibilitySwitch
            renderChildren={(isVisible, show, hide) => (
              <React.Fragment>
                <ul
                  className={styles.list}
                  onClick={(e) => {
                    if (canAllocateResources) {
                      e.stopPropagation();
                      (isVisible ? hide : show)();
                    }
                  }}
                >
                  <ActivityWatcher
                    className={styles.main}
                    focus={canAllocateResources ? show : undefined}
                    blur={canAllocateResources ? hide : undefined}
                  >
                    <React.Fragment>
                      {allocations.slice(0, 3).map(({ resource }) => (
                        <li key={resource.uuid} className={styles.allocation}>
                          <ResourceAvatar resource={resource} />
                        </li>
                      ))}
                      {allocations.slice(3).length > 0 && (
                        <li
                          className={classNames(
                            styles.allocation,
                            styles.summary,
                          )}
                        >
                          <p>+{allocations.slice(3).length}</p>
                        </li>
                      )}
                      {canAllocateResources && allocations.length === 0 && (
                        <li className={styles.allocation}>
                          <div className={styles.menu}>
                            <div>
                              <AddAllocationIcon
                                className={styles.addAllocationIcon}
                              />
                            </div>
                          </div>
                        </li>
                      )}
                    </React.Fragment>
                  </ActivityWatcher>
                </ul>
                {isVisible && (
                  <div className={styles.resourcesList}>
                    <div
                      className={styles.overlay}
                      onClick={(e) => {
                        e.stopPropagation();
                        hide();
                      }}
                    />
                    <div
                      className={styles.quickAllocationMenu}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <ResourceAllocationMenu
                        allocatedResources={allocations.map(
                          (allocation) => allocation.resource,
                        )}
                        select={(resources) => {
                          !isRequestPending &&
                            allocateResources({
                              allocations: resources.map((resource) => ({
                                ...allocation,
                                resource,
                              })),
                              task,
                              project,
                            });
                        }}
                        unSelect={(resources) =>
                          resources.forEach(({ uuid }) => {
                            const allocation = allocations.find(
                              (allocation) => allocation.resource.uuid === uuid,
                            );

                            allocation && removeAllocation(allocation);
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          />
        </div>
      )}
    </Safeguard>
  );
}

export { AllocationsList };
